/* eslint-disable no-restricted-syntax */

import {
    Feature,
    MenuConfiguration,
    MenuItem,
    NewgenusRoute,
    Permission,
    User,
    hasAnyPermissions,
    hasPermission,
    isDeveloper,
    isFeatureAvailableForUser,
} from '@newgenus/common';
import { ModuleSwitchService } from './module-switch.service';
import { isRouteModuleOn, MODULE_FEATURE_KEYS } from '@newgenus/common';
import { areRoutesLoaded, selectRoutesByFeatureKeysOrSystemRoute } from '../../redux/routes/routes.selectors';
import { BehaviorSubject, Observable, Subject, debounceTime, from, map, of, switchMap, take, tap } from 'rxjs';
import { ActivatedRouteSnapshot, Router, RoutesRecognized } from '@angular/router';
import { allRoutesLoaded } from '../../redux/routes/routes.actions';
import { AngularFirestore } from '@newgenus/fire-compat/firestore';
import { StoreUser } from '../../workspace/user.abstract';
import { MenuConfigurations } from '../utils/constants';
import { Store, select } from '@ngrx/store';
import { AuthService } from './auth.service';
import { routeMatchesUrl } from '../../redux/routes/route-match';
import { Injectable } from '@angular/core';

/**
 * Does a routes/* doc cover a menu item? Historically an exact, lower-cased match of
 * `/app/{section}/{path}`; a doc with `matchPrefix` (a module's deep URLs, e.g. the issue
 * tracker's `/app/issues`) also covers every item under that prefix, section roots included.
 */
function routeCoversMenuItem(r: NewgenusRoute, item: MenuItem): boolean {
    const url = `/app/${item.section.toLowerCase()}/${item.path.toLowerCase()}`;
    return routeMatchesUrl({ route: r.route.toLowerCase(), matchPrefix: r.matchPrefix?.toLowerCase() }, url);
}

@Injectable({
    providedIn: 'root',
})

//  This service is responsible for managing user authentication, permissions, and route authorization within the application.
// It integrates with Angular's router, Firestore, and RxJS to provide a robust mechanism for handling security-related functionality.
export class SecurityService extends StoreUser {
    public userAuth: any;
    public permissionCheck$ = new Subject<void>();
    public permissionBehaviorSub$ = new BehaviorSubject<any>(null);
    private currentRecognizedRoute!: RoutesRecognized;

    constructor(
        public auth: AuthService,
        public override store: Store,
        private db: AngularFirestore,
        private router: Router,
        private moduleSwitch: ModuleSwitchService
    ) {
        super(store);
        this.initStoreUser();

        this.router.events.subscribe((event) => {
            if (event instanceof RoutesRecognized) {
                this.currentRecognizedRoute = event;
            }
        });

        this.user$
            .pipe(
                tap((users) => {
                    if (users && users[0]) {
                        this.permissionCheck$.next();
                        this.permissionBehaviorSub$.next(users[0].uid);
                    }
                }),
                debounceTime(500),
                map((users) => (users && users[0] ? users[0] : null)),
                switchMap((user: User | null) => {
                    if (this.auth.isLoggedIn && user?.organizations) {
                        return this.checkRouteAuthorization(this.router.url);
                    } else {
                        return from([false]);
                    }
                })
            )
            .subscribe((isAuthorizedOnCurrentRoute) => {
                if (this.auth.isLoggedIn && isAuthorizedOnCurrentRoute === false) {
                    this.redirectToNearbyAuthorizedRoute();
                } else {
                    // Do nothing, the Auth Guard has got this...
                }
            });
    }

    /**
     * Check if a user has access to a given feature and permission name.
     *
     * @param {string} featureName The name of the feature to check.
     * @param {string} permissionName The name of the permission to check.
     * @return {boolean} True if the user has access to the feature and permission, false otherwise.
     * @memberof SecurityService
     */
    public checkPermission(featureName: Feature, permissionName: Permission) {
        return hasPermission(this.user, featureName, permissionName);

        // // Short circuit to false if no user or organization
        // if (!this.user?.organizations) return false;

        // // Reference current selected org.
        // const org = this.user.organizations[this.user.organizations.selected];

        // // Short circuit to false if no details.
        // if (!org?.features || !org?.featureDetails) {
        //     return false;
        // }

        // // Find feature with feature name.
        // const featureFound = org.features.includes(featureName);

        // // Find permission.
        // const permissionFound = featureFound
        //     ? (org.featureDetails[featureName]).permissions.includes(permissionName)
        //     : false;

        // // Check if feature has permission.
        // const hasAccess = featureFound && permissionFound;

        // return hasAccess;
    }

    /**
     * Check if the user is a developer.
     *
     * @return {boolean} True if the user is a developer, false otherwise.
     */
    public isDeveloper() {
        return isDeveloper(this.user);
    }

    /**
     * Monitor a user's permissions for a given feature and permission name.
     * This will return an observable that will emit a boolean value when the user or their permissions change.
     *
     * @param {string} featureName The name of the feature to check.
     * @param {string} permissionName The name of the permission to check.
     * @return {Observable<boolean>} An observable that will emit a boolean value when the user or their permissions change.
     * @memberof SecurityService
     */
    public monitorPermission(featureName: Feature, permissionName: Permission): Observable<boolean> {
        return new Observable((observer) => {
            this.permissionBehaviorSub$.subscribe((uid) => {
                if (uid) {
                    const hasAccess = this.checkPermission(featureName, permissionName);
                    observer.next(hasAccess);
                }
            });

            this.permissionCheck$.subscribe(() => {
                const hasAccess = this.checkPermission(featureName, permissionName);
                observer.next(hasAccess);
            });
        });
    }

    /**
     * Check if a user has access to a given feature and any of the given permissions.
     * This will return an observable that will emit a boolean value when the user or their permissions change.
     *
     * @param {Feature} featureName The name of the feature to check.
     * @param {...Permission[]} permissions The names of the permissions to check.
     * @return {*}  {Observable<boolean>} An observable that will emit a boolean value when the user or their permissions change.
     * @memberof SecurityService
     */
    public monitorAnyPermissions(featureName: Feature, ...permissions: Permission[]): Observable<boolean> {
        return new Observable((observer) => {
            this.permissionBehaviorSub$.subscribe((uid) => {
                if (uid) {
                    const hasAccess = hasAnyPermissions(this.user, featureName, ...permissions);
                    observer.next(hasAccess);
                }
            });

            this.permissionCheck$.subscribe(() => {
                const hasAccess = hasAnyPermissions(this.user, featureName, ...permissions);
                observer.next(hasAccess);
            });
        });
    }

    /**
     * Check if a user has access to a given feature.
     *
     * @param {string} feature The name of the feature to check.
     * @return {boolean} True if the user has access to the feature, false otherwise.
     * @memberof SecurityService
     */
    public isFeatureAvailableForUser(feature: Feature) {
        return isFeatureAvailableForUser(this.user, feature);

        // const featureArray = this.user.organizations ? this.user.organizations[this.user.organizations.selected].features : [];
        // const lowercased = featureArray.map(name => name.toLowerCase());
        // return lowercased.includes(feature.toLowerCase());
    }

    /**
     * Monitors the availability of a specific feature for the current user.
     *
     * This function returns an observable that emits a boolean value indicating
     * whether the specified feature is accessible to the user. It listens for
     * changes in user permissions and updates the observer with the access status.
     *
     * @param feature - The feature to monitor for user access.
     * @returns An observable that emits `true` if the feature is available to the user,
     *          or `false` otherwise.
     */
    public monitorFeature(feature: Feature): Observable<boolean> {
        return new Observable((observer) => {
            this.permissionBehaviorSub$.subscribe((uid) => {
                if (uid) {
                    const hasAccess = this.isFeatureAvailableForUser(feature);
                    observer.next(hasAccess);
                }
            });

            // this.permissionCheck$.subscribe(() => {
            //     const hasAccess = this.isFeatureAvailableForUser(feature);
            //     observer.next(hasAccess);
            // });
        });
    }

    /**
     * Check if a user has any organization.
     *
     * @return {boolean} True if the user has access to an organization, false otherwise.
     * @memberof SecurityService
     */
    public isOrgAvailableForUser() {
        return this.user.organizations !== undefined && this.user.organizations !== null;
    }

    private extractEmbeddedChildData(children: ActivatedRouteSnapshot[]): Array<EmbeddedChildRoutes> {
        return children.map((c) => {
            return {
                route: c.routeConfig?.path || '',
                data: c.data,
                children: this.extractEmbeddedChildData(c.children),
            };
        });
    }

    /**
     * Checks if the current route is authorized for the user based on various conditions.
     *
     * @param path - The path of the route to check authorization for.
     * @param loopCount - The current iteration count for retrying the authorization check (default is 0).
     * @returns An `Observable` that emits:
     * - `true` if the route is authorized,
     * - `false` if the route is not authorized,
     * - `null` if the route has changed during the check process.
     *
     * ### Behavior:
     * - If the route changes during the check, the function resolves with `null`.
     * - The `/app/home` route is always authorized for all users.
     * - If the route has associated data indicating authorization (`isAuthorizedOnCurrentRoute`), it resolves with `true`.
     * - If child route data allows unauthorized access (`isUnauthorizedAccessAllowed`), it resolves with `true`.
     * - If the user is logged in but their organization data is not yet loaded, the function retries up to 5 times with an 800ms delay.
     * - If the user's organization data is loaded, it checks the user's features against the route's required features.
     * - Developers are granted access to all routes, but a warning is logged if a feature is missing for the route.
     * - If an error occurs during the process, the user is signed out and an alert is shown.
     *
     * ### Notes:
     * - This function uses a mix of synchronous and asynchronous logic, including Promises and Observables.
     * - The function includes a temporary workaround for waiting on route data to load, which should ideally be replaced with an RxJS-based solution.
     *
     * @throws Alerts the user and signs them out if an unexpected error occurs or if the retry limit is exceeded.
     */
    public checkRouteAuthorization(path: string, loopCount = 0): Observable<boolean | null> {
        return from(
            new Promise<boolean | null>((resolve) => {
                // If the route has changed, terminate looping and resolve as null.
                if (this.router.url !== path) {
                    resolve(null);
                    return;
                }

                // Everyone can go home. No, seriously, everyone, can go home. Please go home.
                if (path === '/app/home') {
                    resolve(true);
                    return;
                }

                // Check if the route that is being navigated requires authorization or not.
                if (this.currentRecognizedRoute && this.currentRecognizedRoute.state.url === path) {
                    // Look for data on the current active route segment (this checks the base route, consider '/app/home', this would be just '/').
                    if (Object.keys(this.currentRecognizedRoute.state.root?.data || {}).length > 0) {
                        const routeData = this.currentRecognizedRoute.state.root.data;
                        if (routeData['isAuthorizedOnCurrentRoute']) {
                            resolve(true);
                            return;
                        }
                    } else {
                        // Look for data on the embedded child routes (this for example, on '/app/home' will check both 'app' and 'home' routes as they are children of '/').
                        const routeData = this.extractEmbeddedChildData(
                            this.currentRecognizedRoute.state.root.children
                        );
                        const isUnauthorizedAccessAllowed = this.recursiveCheckChildData(
                            routeData,
                            'isUnauthorizedAccessAllowed'
                        );

                        if (isUnauthorizedAccessAllowed) {
                            console.debug(`Route ${path} allows unauthorized access.`);
                            resolve(true);
                            return;
                        }
                    }
                }

                let routeFeatureExists = false;

                if (this.auth.isLoggedIn) {
                    // If the user's organization field is not set, this means the auth firestore user has not loaded in yet and it will shortly.
                    // Do a loop, but only 5 times.
                    if (!this.user.organizations) {
                        if (loopCount < 5) {
                            // Loop the operation after 800ms.
                            setTimeout(() => {
                                return this.checkRouteAuthorization(this.router.url, loopCount + 1);
                            }, 800);
                        } else {
                            // Either their sus, or their internet is out. Either way, they're signing out.
                            alert('Please check your internet connection and try again.');
                            this.auth.signOut();
                        }
                    }

                    const selectedOrg = this.user.organizations
                        ? this.user.organizations[this.user.organizations.selected]
                        : null;
                    const userFeatureKeyArray = (selectedOrg?.features as string[]) || [];

                    // TODO - this is a hack to get around the fact that the route is not loaded yet.
                    // This should be fixed by using RXJS to wait for the route to be loaded.
                    return this.fetchRoutesByFeatureKeysIncludingSystemRoutes(userFeatureKeyArray)
                        .pipe(take(1))
                        .toPromise()
                        .then((routes) => {
                            const routeFound = routes ? routes.find((r) => routeMatchesUrl(r, path)) : undefined;

                            // Developers can access all routes.
                            if (this.user.isDeveloper || routeFound) {
                                routeFeatureExists = true;

                                if (this.user.isDeveloper && routeFound === undefined)
                                    console.warn(
                                        `Developer user <${this.user?.email}> is missing feature <${routeFound}> for route <${path}>.`
                                    );
                                // console.warn(`Developer user <${this.user?.email}> is missing feature <${routeFound?.routeFeature}> for route <${path}>.`);
                            } else {
                                routeFeatureExists = false;
                            }

                            resolve(routeFeatureExists);
                        })
                        .catch((err) => {
                            console.error('checkRouteAuthorization > err: ', err);
                            alert('Unexpected error occurred. Please try again.');
                            this.auth.signOut();
                        });
                } else {
                    resolve(false);
                    return;
                }
            })
        );
    }

    private recursiveCheckChildData(routeData: EmbeddedChildRoutes[], field: string): any {
        for (let i = 0; i < routeData.length; i++) {
            const route = routeData[i];
            if (Object.keys(route?.data || {}).length > 0 && route.data[field] !== undefined) {
                return route.data[field];
            } else {
                if (route.children.length > 0) {
                    return this.recursiveCheckChildData(route.children, field);
                }
            }
        }
    }

    /** Menu memoization: rebuilt only when user / selected org / features change. */
    private menuCacheKey: string | null = null;
    private menuCache: MenuConfiguration | null = null;
    private menuInFlight: { key: string; promise: Promise<MenuConfiguration | null> } | null = null;

    /** Moves when the module switchboard changes - menu consumers rebuild on it (see ModuleSwitchService.version$). */
    public get moduleVersion$(): Observable<number> {
        return this.moduleSwitch?.version$ ?? of(0);
    }

    /**
     * The menu this resolves with is FULLY built — callers may match the active
     * route against it immediately. (It used to resolve before the async route
     * fetch filled the menu in, which left the mobile bar / sidebar highlight
     * blank for seconds until a later user$ emission re-ran the chain.)
     *
     * Called by app.component, sidebar and home on every user emission, so the
     * result is memoized per user/org/features and concurrent callers share one
     * in-flight build.
     */
    public async generateSubMenuFromUserAccess(): Promise<MenuConfiguration | null> {
        if (!this.user) return null;

        const selectedOrg = this.user.organizations ? this.user.organizations[this.user.organizations.selected] : null;
        const featureArray = (selectedOrg?.features as string[]) || [];
        const cacheKey = [
            this.user.uid,
            this.user.isDeveloper ? 'dev' : 'std',
            this.user.organizations?.selected ?? '',
            featureArray.join(','),
            'm' + (this.moduleSwitch?.version ?? 0),
        ].join('|');

        if (this.menuCache && this.menuCacheKey === cacheKey) return this.menuCache;
        if (this.menuInFlight?.key === cacheKey) return this.menuInFlight.promise;

        const promise = this.buildMenuFromUserAccess(featureArray);
        this.menuInFlight = { key: cacheKey, promise };
        try {
            const menu = await promise;
            if (menu) {
                this.menuCache = menu;
                this.menuCacheKey = cacheKey;
            }
            return menu;
        } finally {
            if (this.menuInFlight?.key === cacheKey) this.menuInFlight = null;
        }
    }

    private async buildMenuFromUserAccess(featureArray: string[]): Promise<MenuConfiguration | null> {
        const userMenuConfigurations: MenuConfiguration = {
            HOME: {
                header: MenuConfigurations['HOME'].header,
                showFolders: MenuConfigurations['HOME'].showFolders,
                topTierMenuItems: [],
                middleTierMenuItems: [],
            },
        };

        // Dark-deploy switchboard (DARK-DEPLOY-MODULE-SWITCHES-DESIGN.md §3b): a route whose module is
        // dark leaves the menu — for developers too (a developer sees a dark module only where it says
        // allowDevelopers). Wait for the first switch snapshot so the fallback surface is not used on
        // first paint.
        await this.moduleSwitch.ready();
        const moduleCtx = {
            orgId: this.user.organizations?.selected,
            userFeatures: featureArray,
            isDeveloper: !!this.user.isDeveloper,
        };
        // A system route (section dashboard) is open when ANY of its modules is on (module-route.ts).
        const isRouteOn = (r: NewgenusRoute): boolean =>
            isRouteModuleOn(r, (m) => this.moduleSwitch.isOn(m, moduleCtx));

        if (this.user.isDeveloper) {
            // Developers can do what they want in this world — minus dark modules.
            console.warn('Developer mode enabled. All menu items are available (minus dark modules).');
            // Fetch every module's routes so dark ones can be identified; menu items with no route
            // document (dev-only entries) stay, as before.
            const allKeys = Array.from(new Set(Object.values(MODULE_FEATURE_KEYS).flat()));
            const devRoutes =
                (await this.fetchRoutesByFeatureKeysIncludingSystemRoutes(allKeys).pipe(take(1)).toPromise()) ?? [];
            const dark = devRoutes.filter((r) => !isRouteOn(r));
            if (dark.length === 0) return MenuConfigurations;
            const pruned: MenuConfiguration = {};
            for (const [k, sub] of Object.entries(MenuConfigurations)) {
                const keep = (item: MenuItem) => !dark.some((r) => routeCoversMenuItem(r, item));
                pruned[k] = {
                    ...sub,
                    topTierMenuItems: sub.topTierMenuItems.filter(keep),
                    middleTierMenuItems: sub.middleTierMenuItems.filter(keep),
                };
            }
            return pruned;
        }

        const routes = (
            (await this.fetchRoutesByFeatureKeysIncludingSystemRoutes(featureArray).pipe(take(1)).toPromise()) ?? []
        ).filter(isRouteOn);

        for (const [subDirectoryKey, subMenu] of Object.entries(MenuConfigurations)) {
            // Match TopTierMenuItems to featureKey.
            const topTierMenuItems = subMenu.topTierMenuItems.filter((item) =>
                routes?.some((r) => routeCoversMenuItem(r, item))
            );

            // Match MiddleTierMenuItems to featureKey.
            const middleTierMenuItems = subMenu.middleTierMenuItems.filter((item) =>
                routes?.some((r) => routeCoversMenuItem(r, item))
            );

            // Merge topTierMenuItems and middleTierMenuItems into existing userMenuConfigurations.
            if (topTierMenuItems.length > 0 || middleTierMenuItems.length > 0) {
                if (userMenuConfigurations[subDirectoryKey]) {
                    topTierMenuItems
                        .filter(
                            (x) =>
                                userMenuConfigurations[subDirectoryKey].topTierMenuItems.find((y) => y.id === x.id) ===
                                undefined
                        )
                        .forEach((x) => userMenuConfigurations[subDirectoryKey].topTierMenuItems.push(x));
                    middleTierMenuItems
                        .filter(
                            (x) =>
                                userMenuConfigurations[subDirectoryKey].middleTierMenuItems.find(
                                    (y) => y.id === x.id
                                ) === undefined
                        )
                        .forEach((x) => userMenuConfigurations[subDirectoryKey].middleTierMenuItems.push(x));
                } else {
                    // Add one of each sub directory to the home directory — unless HOME already
                    // carries this section's own root entry (constants.ts HOME lists 'issues',
                    // whose route doc matches by prefix, so it survived the HOME pass above);
                    // pushing a renamed copy of the first item as well showed the section twice
                    // in the sidebar for every non-developer (OE-189).
                    const section = topTierMenuItems[0]?.section ?? middleTierMenuItems[0]?.section;
                    const homeHasSection = userMenuConfigurations['HOME'].topTierMenuItems.some(
                        (y) => y.section === section
                    );
                    if (!homeHasSection && topTierMenuItems.length > 0) {
                        userMenuConfigurations['HOME'].topTierMenuItems.push(
                            topTierMenuItems.slice(0, 1).map((m) => {
                                m = { ...m };
                                m.name = subMenu.header;
                                m.showCount = false;
                                m.icon = this.toHomeIcon(m);
                                m.svg = undefined;
                                // Keep the section where HOME lists it, not where its first page sorts
                                // (Organization arrived via its dashboard, order 41, below Issues).
                                m.order =
                                    MenuConfigurations['HOME'].topTierMenuItems.find((h) => h.section === m.section)
                                        ?.order ?? m.order;
                                return m;
                            })[0]
                        );
                    }

                    userMenuConfigurations[subDirectoryKey] = {
                        header: subMenu.header,
                        showFolders: subMenu.showFolders,
                        topTierMenuItems,
                        middleTierMenuItems,
                    };
                }
            }
        }

        // Sort the HOME top tier menu items.
        userMenuConfigurations['HOME'].topTierMenuItems = userMenuConfigurations['HOME'].topTierMenuItems.sort(
            (a, b) => a.order - b.order
        );

        return userMenuConfigurations;
    }

    private toHomeIcon(m: MenuItem): string {
        switch (m.section) {
            case 'workspace':
                return 'account_balance_wallet';
            case 'management-tools':
                return 'engineering';
            case 'organization':
                return 'corporate_fare';
            case 'issues':
                return 'bug_report';
            default:
                return 'dashboard';
        }
    }

    /**
     * Fetches routes based on the provided feature keys, including system routes.
     *
     * This function checks if the routes are already loaded in the store. If they are,
     * it retrieves the matching routes from the store. Otherwise, it fetches all routes
     * from Firestore, dispatches them to the store, and filters the matching routes
     * based on the provided feature keys.
     *
     * @param featureKeys - An array of feature keys to filter the routes.
     * @returns An observable that emits an array of `NewgenusRoute` objects matching the feature keys.
     */
    private fetchRoutesByFeatureKeysIncludingSystemRoutes(featureKeys: string[]): Observable<NewgenusRoute[]> {
        return this.store.pipe(select(areRoutesLoaded)).pipe(
            take(1),
            switchMap((routesAreLoaded) => {
                if (routesAreLoaded) {
                    return (
                        this.store
                            // Fetch all matching routes.
                            .pipe(select(selectRoutesByFeatureKeysOrSystemRoute(featureKeys)))
                            .pipe(map((resp) => ({ routes: resp, from: 'ngrx' })))
                    );
                } else {
                    return this.db
                        .collection<NewgenusRoute>('routes')
                        .get()
                        .pipe(
                            map((routes) => {
                                // Parse from Firestore Documents.
                                const allRoutes = routes.docs.map((doc) => doc.data());

                                // Dispatch the fetched routes to the store.
                                this.store.dispatch(allRoutesLoaded({ routes: allRoutes }));

                                // Filter matching routes.
                                const matchingRoutes = allRoutes.filter((routeDoc) => {
                                    return (
                                        routeDoc.publicRoute === true || // any signed-in user
                                        featureKeys.includes(routeDoc.routeFeature) ||
                                        routeDoc.features.every((f) => featureKeys.includes(f)) ||
                                        (routeDoc.systemRoute &&
                                            routeDoc.features.some((rFeatureKey) => featureKeys.includes(rFeatureKey)))
                                    );
                                });

                                // Return the matching routes.
                                return { routes: matchingRoutes, from: 'firestore' };
                            })
                        );
                }
            }),
            map((results) => results.routes)
        );
    }

    private redirectToNearbyAuthorizedRoute(): void {
        this.generateSubMenuFromUserAccess().then((newMenu) => {
            const url = this.router.url;
            let moduleFragment = url.substring(url.indexOf('/', 1) + 1, url.indexOf('/', url.indexOf('/', 1) + 1));
            moduleFragment = moduleFragment.replace('/', '').replace('/', '');

            moduleFragment = moduleFragment.toUpperCase();

            if (newMenu && moduleFragment) {
                let path = '/app/home';

                if (newMenu[moduleFragment]?.topTierMenuItems?.length > 0) {
                    const topMenuItem = newMenu[moduleFragment].topTierMenuItems[0];
                    path =
                        `app/${topMenuItem.section}` + (topMenuItem.path ? `/${topMenuItem.path.toLowerCase()}` : '');
                } else if (newMenu[moduleFragment]?.middleTierMenuItems?.length > 0) {
                    const middleMenuItem = newMenu[moduleFragment].middleTierMenuItems[0];
                    path =
                        `app/${middleMenuItem.section}` +
                        (middleMenuItem.path ? `/${middleMenuItem.path.toLowerCase()}` : '');
                }

                this.router.navigate([path]);
            } else {
                // Do nothing...
                // This is because angular routing will handle the redirect.
            }
        });
    }
}

interface EmbeddedChildRoutes {
    route: string;
    data: Record<string, any>;
    children: Array<EmbeddedChildRoutes>;
}
